import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  X, Stethoscope, Video, MapPin, Pencil, CalendarClock,
  Trash2, ChevronLeft, Check, TriangleAlert, Minus, Plus, Info,
  Calendar, Phone, Mail, User, Clock, RefreshCw,
} from 'lucide-react';
import { format, addMinutes, differenceInMinutes } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { getEstadoCitaInfo, puedeTransicionar } from '@/types/cita';
import { TareasRelacionadasPanel } from '@/components/tareas/TareasRelacionadasPanel';
import { usePermisosTareas } from '@/hooks/useTareas';

/* ── Constants ─────────────────────────────────────────────────────────────── */

const RECURRENCIA_LABEL: Record<string, string> = {
  semanal: 'Semanal', quincenal: 'Quincenal',
  mensual: 'Mensual', trimestral: 'Trimestral',
};

const TIME_SLOTS_R: string[] = [];
for (let h = 6; h <= 22; h++) {
  for (const m of [0, 30]) {
    if (h === 22 && m === 30) break;
    TIME_SLOTS_R.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

type View = 'detail' | 'reschedule' | 'confirm-delete';
type Tab  = 'cita' | 'clinica' | 'tareas';

const ETIQUETA_TAB: Record<Tab, string> = { cita: 'Cita', clinica: 'Clínica', tareas: 'Tareas' };

/* ── Props ─────────────────────────────────────────────────────────────────── */

interface CitaDetailDialogProps {
  cita: any | null;
  onClose: () => void;
  canIniciarConsulta?: boolean;
  onEditar: (cita: any) => void;
  onConfirmar: (citaId: string) => void;
  onNoAsistio: (citaId: string) => void;
  onReagendar: (citaId: string, fechaInicio: string, fechaFin: string) => void;
  onEliminar: (citaId: string) => void;
  onIniciar: (cita: any) => void;
  onVerConsulta?: (cita: any) => void;
  isUpdating?: boolean;
  isIniciando?: boolean;
  isEliminating?: boolean;
}

/* ── Helpers ───────────────────────────────────────────────────────────────── */

function snapToSlot(h: string): string {
  const [hh, mm] = h.split(':').map(Number);
  const snapped = mm >= 30
    ? `${String(hh).padStart(2, '0')}:30`
    : `${String(hh).padStart(2, '0')}:00`;
  return TIME_SLOTS_R.includes(snapped) ? snapped : '09:00';
}

function initials(paciente: any): string {
  return `${paciente?.nombre?.[0] ?? ''}${paciente?.apellidoPaterno?.[0] ?? ''}`.toUpperCase() || 'P';
}

/* ── MetaRow ───────────────────────────────────────────────────────────────── */

function MetaRow({ icon: Icon, label, value, iconColor = 'text-primary-500' }: {
  icon: any; label: string; value: string; iconColor?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-b-0">
      <div className="w-8 h-8 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
        <Icon size={14} className={iconColor} />
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <p className="text-[10px] font-bold uppercase tracking-widest text-primary-400 leading-none mb-1">
          {label}
        </p>
        <p className="text-sm font-medium text-text-primary leading-snug">{value}</p>
      </div>
    </div>
  );
}

/* ── Main component ────────────────────────────────────────────────────────── */

export function CitaDetailDialog({
  cita, onClose, canIniciarConsulta,
  onEditar, onConfirmar, onNoAsistio, onReagendar, onEliminar, onIniciar, onVerConsulta,
  isUpdating, isIniciando, isEliminating,
}: CitaDetailDialogProps) {
  const [view, setView] = useState<View>('detail');
  const [tab, setTab]   = useState<Tab>('cita');
  const { puedeUsar: puedeUsarTareas } = usePermisosTareas();
  const [rFecha, setRFecha]     = useState('');
  const [rHora, setRHora]       = useState('');
  const [rDuracion, setRDuracion] = useState(30);

  useEffect(() => {
    if (cita) {
      setView('detail');
      setTab('cita');
      setRFecha(format(new Date(cita.fechaInicio), 'yyyy-MM-dd'));
      setRHora(snapToSlot(format(new Date(cita.fechaInicio), 'HH:mm')));
      setRDuracion(Math.max(15, differenceInMinutes(new Date(cita.fechaFin), new Date(cita.fechaInicio))));
    }
  }, [cita?.id]);

  if (!cita) return null;

  const canIniciar  = canIniciarConsulta && cita.estado === 'confirmada';
  const canManage   = ['programada', 'confirmada'].includes(cita.estado);
  const canEliminar = cita.estado !== 'en_curso';
  const isEnCurso   = cita.estado === 'en_curso';
  const puedeNoAsistio = puedeTransicionar(cita.estado, 'no_asistio');
  const estadoInfo  = getEstadoCitaInfo(cita.estado);

  const nombrePaciente = cita.paciente
    ? `${cita.paciente.nombre} ${cita.paciente.apellidoPaterno}`
    : 'Paciente';
  const correo   = cita.paciente?.correo ?? cita.paciente?.email ?? '';
  const telefono = cita.paciente?.telefono ?? '';
  const duracion = differenceInMinutes(new Date(cita.fechaFin), new Date(cita.fechaInicio));
  const refCode  = `#${cita.id?.slice(0, 8).toUpperCase() ?? '--------'}`;

  function handleSaveReagendar() {
    const start = new Date(`${rFecha}T${rHora}:00`);
    const end   = addMinutes(start, rDuracion);
    onReagendar(cita.id, start.toISOString(), end.toISOString());
  }

  const rEnd = rFecha && rHora
    ? format(addMinutes(new Date(`${rFecha}T${rHora}:00`), rDuracion), 'HH:mm')
    : '';

  return (
    <Dialog.Root open={!!cita} onOpenChange={open => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-sm focus:outline-none">

          {/* ── DETAIL VIEW ──────────────────────────────────────────────────── */}
          {view === 'detail' && (
            <div className="bg-white rounded-2xl shadow-2xl flex flex-col max-h-[88vh] overflow-hidden">

              {/* Header */}
              <div className="flex items-start justify-between px-5 pt-5 pb-3 flex-shrink-0">
                <div>
                  <Dialog.Title className="text-base font-bold text-text-primary leading-tight">
                    Detalle de cita
                  </Dialog.Title>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs font-mono font-semibold text-gray-400 bg-gray-50 rounded-lg px-2 py-0.5">
                      {refCode}
                    </span>
                    <span className={cn('text-xs px-2.5 py-0.5 rounded-full font-semibold', estadoInfo.softCls)}>
                      {estadoInfo.label}
                    </span>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors mt-0.5"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Tab bar */}
              <div className="flex px-5 border-b border-gray-100 flex-shrink-0">
                {(puedeUsarTareas ? ['cita', 'clinica', 'tareas'] as const : ['cita', 'clinica'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={cn(
                      'pb-2.5 mr-6 text-sm font-semibold border-b-2 transition-colors',
                      tab === t
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-text-secondary hover:text-text-primary',
                    )}
                  >
                    {ETIQUETA_TAB[t]}
                  </button>
                ))}
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">

                {/* ── TAB: CITA ──────────────────────────────────────────── */}
                {tab === 'cita' && (
                  <div className="space-y-4">

                    {/* Patient */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center text-base font-bold text-primary-600 flex-shrink-0 ring-2 ring-primary-200">
                        {initials(cita.paciente)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-text-primary text-base leading-tight">{nombrePaciente}</p>
                        {correo && (
                          <p className="text-xs text-text-secondary mt-1 flex items-center gap-1.5">
                            <Mail size={11} className="text-primary-400 flex-shrink-0" />
                            <span className="truncate">{correo}</span>
                          </p>
                        )}
                        {telefono && (
                          <p className="text-xs text-text-secondary mt-0.5 flex items-center gap-1.5">
                            <Phone size={11} className="text-primary-400 flex-shrink-0" />
                            {telefono}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-gray-100" />

                    {/* Date / time / duration */}
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2.5 text-sm">
                        <Calendar size={15} className="text-primary-400 flex-shrink-0" />
                        <span className="capitalize font-medium text-text-primary">
                          {format(new Date(cita.fechaInicio), "EEEE d 'de' MMMM, yyyy", { locale: es })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5 text-sm">
                        <Clock size={15} className="text-primary-400 flex-shrink-0" />
                        <span className="font-medium text-text-primary">
                          {format(new Date(cita.fechaInicio), 'hh:mm a')} – {format(new Date(cita.fechaFin), 'hh:mm a')}
                        </span>
                        <span className="text-xs text-text-secondary">({duracion} min)</span>
                      </div>
                      {cita.esRecurrente && cita.recurrencia && (
                        <div className="flex items-center gap-2">
                          <RefreshCw size={13} className="text-primary-400 flex-shrink-0" />
                          <span className="text-xs text-primary-600 font-medium bg-primary-50 rounded-full px-2 py-0.5">
                            Recurrente · {RECURRENCIA_LABEL[cita.recurrencia] ?? cita.recurrencia}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Primary actions: confirmar / iniciar */}
                    {(cita.estado === 'programada' || canIniciar) && (
                      <div className="flex gap-2">
                        {cita.estado === 'programada' && (
                          <button
                            onClick={() => onConfirmar(cita.id)}
                            disabled={isUpdating}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50"
                          >
                            <Check size={14} />
                            Confirmar
                          </button>
                        )}
                        {canIniciar && (
                          <button
                            onClick={() => onIniciar(cita)}
                            disabled={isIniciando}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
                          >
                            <Stethoscope size={14} />
                            {isIniciando ? 'Iniciando...' : 'Iniciar consulta'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* En curso: notice + access button */}
                    {isEnCurso && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-amber-700 bg-amber-50 rounded-xl px-3 py-2 text-xs font-medium">
                          <Stethoscope size={12} className="flex-shrink-0" />
                          Consulta en progreso
                        </div>
                        {onVerConsulta && (
                          <button
                            onClick={() => onVerConsulta(cita)}
                            className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 transition-colors"
                          >
                            <Stethoscope size={14} />
                            Ir a la consulta
                          </button>
                        )}
                      </div>
                    )}

                    {/* Confirmada notice */}
                    {cita.estado === 'confirmada' && (
                      <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 rounded-xl px-3 py-2.5">
                        <Info size={12} className="flex-shrink-0 mt-px" />
                        Editar o reagendar esta cita requerirá confirmación nuevamente
                      </div>
                    )}
                  </div>
                )}

                {/* ── TAB: TAREAS ────────────────────────────────────────── */}
                {tab === 'tareas' && puedeUsarTareas && (
                  <TareasRelacionadasPanel
                    compacta
                    entidadTipo="cita"
                    entidadId={cita.id}
                    etiqueta={`Cita · ${format(new Date(cita.fechaInicio), "d MMM yyyy", { locale: es })}`}
                  />
                )}

                {/* ── TAB: CLÍNICA ───────────────────────────────────────── */}
                {tab === 'clinica' && (
                  <div className="-my-0">
                    {cita.clinica && (
                      <MetaRow icon={MapPin} label="Clínica" value={cita.clinica.nombre} />
                    )}
                    {cita.medico && (
                      <MetaRow icon={Stethoscope} label="Médico asignado" value={cita.medico.nombre} />
                    )}
                    <MetaRow
                      icon={cita.tipo === 'teleconsulta' ? Video : User}
                      label="Tipo de cita"
                      value={cita.tipo === 'teleconsulta' ? 'Teleconsulta' : 'Presencial'}
                      iconColor={cita.tipo === 'teleconsulta' ? 'text-violet-500' : 'text-primary-500'}
                    />
                    {(cita.motivo || cita.notas) && (
                      <MetaRow
                        icon={Pencil}
                        label="Notas"
                        value={[cita.motivo, cita.notas].filter(Boolean).join(' · ')}
                      />
                    )}
                    {!cita.clinica && !cita.medico && !cita.motivo && !cita.notas && (
                      <p className="text-sm text-text-secondary text-center py-8">
                        Sin información adicional
                      </p>
                    )}
                  </div>
                )}

              </div>

              {/* Footer – action buttons */}
              {(canManage || canEliminar || puedeNoAsistio) && (
                <div className="flex-shrink-0 border-t border-gray-100 px-5 py-3 space-y-2">
                  <div className="flex gap-2">
                    {canManage && (
                      <>
                        <button
                          onClick={() => onEditar(cita)}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-text-secondary hover:bg-gray-50 hover:text-primary-600 hover:border-primary-200 transition-colors"
                        >
                          <Pencil size={14} />
                          Editar
                        </button>
                        <button
                          onClick={() => setView('reschedule')}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-text-secondary hover:bg-gray-50 hover:text-primary-600 hover:border-primary-200 transition-colors"
                        >
                          <CalendarClock size={14} />
                          Reagendar
                        </button>
                      </>
                    )}
                    {canEliminar && (
                      <button
                        onClick={() => setView('confirm-delete')}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-text-secondary hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors"
                      >
                        <Trash2 size={14} />
                        Eliminar
                      </button>
                    )}
                  </div>
                  {puedeNoAsistio && (
                    <button
                      onClick={() => onNoAsistio(cita.id)}
                      disabled={isUpdating}
                      className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-orange-200 text-sm font-medium text-orange-600 hover:bg-orange-50 transition-colors disabled:opacity-50"
                    >
                      <Clock size={14} />
                      Marcar como no asistió
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── RESCHEDULE VIEW ──────────────────────────────────────────────── */}
          {view === 'reschedule' && (
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 pt-5 pb-4 border-b border-gray-100">
                <button onClick={() => setView('detail')} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                  <ChevronLeft size={18} />
                </button>
                <Dialog.Title className="text-base font-semibold text-text-primary">
                  Reagendar cita
                </Dialog.Title>
                <button onClick={onClose} className="ml-auto p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                  <X size={18} />
                </button>
              </div>

              <div className="px-5 py-5 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-text-primary mb-1.5">Nueva fecha</label>
                  <input
                    type="date"
                    value={rFecha}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    onChange={e => setRFecha(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-text-primary mb-1.5">Hora de inicio</label>
                  <select
                    value={rHora}
                    onChange={e => setRHora(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                  >
                    {TIME_SLOTS_R.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-text-primary mb-1.5">Duración</label>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setRDuracion(d => Math.max(15, d - 15))}
                      className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors">
                      <Minus size={14} />
                    </button>
                    <span className="text-sm font-medium text-text-primary w-20 text-center">{rDuracion} min</span>
                    <button type="button" onClick={() => setRDuracion(d => Math.min(240, d + 15))}
                      className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors">
                      <Plus size={14} />
                    </button>
                    {rEnd && <span className="text-xs text-text-secondary">hasta {rEnd}</span>}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 px-5 pb-5">
                <button onClick={() => setView('detail')}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-text-secondary hover:bg-gray-50 transition-colors">
                  Cancelar
                </button>
                <button onClick={handleSaveReagendar} disabled={!rFecha || !rHora || isUpdating}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors">
                  <Check size={14} />
                  {isUpdating ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          )}

          {/* ── DELETE CONFIRM VIEW ──────────────────────────────────────────── */}
          {view === 'confirm-delete' && (
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 pt-5 pb-4 border-b border-gray-100">
                <button onClick={() => setView('detail')} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                  <ChevronLeft size={18} />
                </button>
                <Dialog.Title className="text-base font-semibold text-text-primary">
                  Eliminar cita
                </Dialog.Title>
                <button onClick={onClose} className="ml-auto p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                  <X size={18} />
                </button>
              </div>

              <div className="px-5 py-6 flex flex-col items-center text-center gap-4">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                  <TriangleAlert size={22} className="text-red-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-primary">¿Eliminar esta cita?</p>
                  <p className="text-xs text-text-secondary mt-1 leading-relaxed">
                    La cita de <span className="font-medium">{nombrePaciente}</span> el{' '}
                    {format(new Date(cita.fechaInicio), "d 'de' MMMM", { locale: es })} a las{' '}
                    {format(new Date(cita.fechaInicio), 'HH:mm')} será eliminada permanentemente.
                  </p>
                </div>
              </div>

              <div className="flex gap-2 px-5 pb-5">
                <button onClick={() => setView('detail')}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-text-secondary hover:bg-gray-50 transition-colors">
                  Cancelar
                </button>
                <button onClick={() => onEliminar(cita.id)} disabled={isEliminating}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 disabled:opacity-50 transition-colors">
                  <Trash2 size={14} />
                  {isEliminating ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          )}

        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
